/**
 * PasswordSetupPage Component
 * 신규 비밀번호 설정 페이지
 * VROONG 브랜드 통일 디자인
 */

import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePasswordSetup } from '../hooks/usePasswordSetup';
import styles from '../styles/RegistrationPage.module.css';

const PasswordSetupPage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const fromFindPassword = searchParams.get('from') === 'find-password';

    const [email, setEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [showNewPassword, setShowNewPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const { handlePasswordSetup, isLoading, error } = usePasswordSetup();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        // 비밀번호 찾기에서 온 경우 email은 빈 문자열로, type은 'changePassword'로 전달
        // 아닌 경우 type은 'newPassword'로 전달
        const type = fromFindPassword ? 'changePassword' : 'newPassword';
        await handlePasswordSetup(fromFindPassword ? '' : email, newPassword, confirmPassword, type);
    };

    const handleBackToLogin = () => {
        navigate('/login');
    };

    return (
        <div className={styles['registration-screen']}>
            {/* 헤더 */}
            <div className={styles['registration-header']}>
                <div className={styles['header-content']}>
                    <button className={styles['back-btn']} onClick={handleBackToLogin}>
                        <i className="fas fa-arrow-left"></i>
                    </button>
                    <h1 className={styles['header-title']}>
                        {fromFindPassword ? '비밀번호 변경' : '비밀번호 설정'}
                    </h1>
                    <div className={styles['header-spacer']}></div>
                </div>
            </div>

            {/* 메인 컨텐츠 */}
            <div className={styles['registration-container']}>
                <div className={styles['card']}>
                    <div className={styles['card-header']}>
                        <div className={styles['card-icon']}>
                            <i className="fas fa-key"></i>
                        </div>
                        <h2 className={styles['card-title']}>
                            {fromFindPassword ? '비밀번호 변경' : '비밀번호 설정'}
                        </h2>
                        <p className={styles['card-description']}>
                            신규 비밀번호를 설정해주세요.
                        </p>
                    </div>

                    <form onSubmit={handleSubmit} className={styles['form-section']}>
                        {!fromFindPassword && (
                            <div className={styles['form-field']}>
                                <label className={styles['field-label']}>
                                    이메일 <span className={styles['required']}>*</span>
                                </label>
                                <div className={styles['input-wrapper']}>
                                    <i className={`fas fa-envelope ${styles['input-icon']}`}></i>
                                    <input
                                        type="email"
                                        className={styles['input-field']}
                                        placeholder="이메일을 입력해주세요"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        disabled={isLoading}
                                    />
                                </div>
                            </div>
                        )}

                        <div className={styles['form-field']}>
                            <label className={styles['field-label']}>
                                신규 비밀번호 <span className={styles['required']}>*</span>
                            </label>
                            <div className={styles['input-wrapper']}>
                                <i className={`fas fa-lock ${styles['input-icon']}`}></i>
                                <input
                                    type={showNewPassword ? 'text' : 'password'}
                                    className={styles['input-field']}
                                    placeholder="신규 비밀번호를 입력해주세요"
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    className={styles['password-toggle']}
                                    onClick={() => setShowNewPassword(!showNewPassword)}
                                >
                                    <i className={`fas ${showNewPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                            <span className={styles['helper-text']}>숫자, 영문, 특수문자 포함 8자리 이상</span>
                        </div>

                        <div className={styles['form-field']}>
                            <label className={styles['field-label']}>
                                비밀번호 확인 <span className={styles['required']}>*</span>
                            </label>
                            <div className={styles['input-wrapper']}>
                                <i className={`fas fa-lock ${styles['input-icon']}`}></i>
                                <input
                                    type={showConfirmPassword ? 'text' : 'password'}
                                    className={styles['input-field']}
                                    placeholder="비밀번호를 다시 입력해주세요"
                                    value={confirmPassword}
                                    onChange={(e) => setConfirmPassword(e.target.value)}
                                    required
                                    disabled={isLoading}
                                />
                                <button
                                    type="button"
                                    className={styles['password-toggle']}
                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    <i className={`fas ${showConfirmPassword ? 'fa-eye-slash' : 'fa-eye'}`}></i>
                                </button>
                            </div>
                        </div>

                        {error && (
                            <div className={styles['error-message']}>
                                <i className="fas fa-exclamation-circle"></i>
                                {error}
                            </div>
                        )}

                        <button type="submit" className={styles['btn-primary']} disabled={isLoading}>
                            {isLoading ? (
                                <>
                                    <span className={styles['btn-spinner']}></span>
                                    처리 중...
                                </>
                            ) : '비밀번호 변경'}
                        </button>

                        <button
                            type="button"
                            className={styles['btn-secondary']}
                            onClick={handleBackToLogin}
                        >
                            로그인 페이지로 돌아가기
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default PasswordSetupPage;
